/**
 * Payment source tracking via utm_source URL parameter.
 *
 * Reads `?utm_source=X` once on page load, persists to sessionStorage,
 * and provides a safe accessor (with whitelist + default fallback).
 */

const STORAGE_KEY = 'felo_utm_source';
const DEFAULT_SOURCE = 'web_home';
// 白名单值是跨 repo 合约——必须与 subtitle-desktop-v2 / felo-subtitle-plugin
// 发出的 utm_source 保持一致。规范参见:
// docs/superpowers/specs/2026-05-25-payment-utm-tracking-design.md §4
const VALID_SOURCES = [
  'desktop_mac',
  'desktop_windows',
  'chrome_extension',
  'edge_extension',
  'web_home'
] as const;

type ValidSource = typeof VALID_SOURCES[number];

const isValid = (v: string | null): v is ValidSource =>
  v !== null && (VALID_SOURCES as readonly string[]).includes(v);

/**
 * Call once on page entry. Reads `utm_source` from URL and writes to sessionStorage
 * if it matches the whitelist. Invalid values are ignored (existing storage value preserved).
 */
export function readAndPersistUtmSource(): void {
  if (typeof window === 'undefined') return;
  const fromUrl = new URL(window.location.href).searchParams.get('utm_source');
  if (isValid(fromUrl)) {
    try {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
    } catch (e) {
      console.warn('utmSource: failed to write sessionStorage', e);
    }
  }
}

/**
 * Returns the current utm_source, falling back to the default if none persisted
 * or storage is unavailable (SSR).
 */
export function getUtmSource(): string {
  if (typeof window === 'undefined') return DEFAULT_SOURCE;
  try {
    return sessionStorage.getItem(STORAGE_KEY) || DEFAULT_SOURCE;
  } catch {
    return DEFAULT_SOURCE;
  }
}
